import type {
  HandoffContext,
  HandoffContextBlock,
  HandoffContextStatus,
} from 'v2/types/api/handoff-context'
import type { QNRFormValues } from './types'

export type NoteField = NonNullable<QNRFormValues['context']>['fields'][number]

export const NOTE_FIELDS: { key: NoteField; label: string }[] = [
  { key: 'transfer_reason', label: 'Transfer reason' },
  { key: 'summary', label: 'Conversation summary' },
  { key: 'collected_fields', label: 'Collected details' },
  { key: 'transcript', label: 'Transcript' },
]

/** `[]` means every field. A narrowed list always keeps `original_caller`: the popup, header,
 * inbox and history read the caller's number through the same filter, and it is not a choice. */
export const toggleNoteField = (fields: NoteField[], field: NoteField): NoteField[] => {
  const all = NOTE_FIELDS.map((f) => f.key)
  const shown = fields.length ? all.filter((f) => fields.includes(f)) : all
  const next = shown.includes(field) ? shown.filter((f) => f !== field) : [...shown, field]
  if (!next.length) return fields
  return next.length === all.length ? [] : ['original_caller', ...next]
}

// Only HS writes the policy this setup configures; hide while profile/rollout is unavailable.
export const canConfigureHandoffContext = (enabled: boolean | undefined, flowType?: string) =>
  enabled === true && (flowType === 'incoming_call' || flowType === 'omni')

export const UPLOAD_PATH = 'v1/handoff-context'
export const SIP_HEADER = 'X-PH-HandoffID'
export const NOT_RECEIVED_GRACE_MS = 45_000

/** "collected_fields" -> "Collected fields". */
export const fieldLabel = (field: string): string =>
  field.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())

/** The one shared regional upload URL: same for every Flow of the organization. */
export const uploadUrl = (resolvedApiUrl: string): string =>
  `${resolvedApiUrl.replace(/\/?$/, '/')}${UPLOAD_PATH}`

export const EXAMPLE_REQUEST = `{
  "schema_version": "1.0",
  "revision": 1,
  "handoff_id": "7f92a136cc204830a0459dc12c4a519b",
  "original_caller": "+14155550123",
  "transfer_to": "+14155550142",
  "transfer_from": "+12025550100",
  "transfer_reason": "Address change needs a human",
  "summary": "Wants to change the delivery address for order 4417.",
  "collected_fields": { "order_id": "4417", "new_city": "Austin" },
  "transcript": [
    { "speaker": "customer", "text": "I need to change my delivery address" },
    { "speaker": "agent", "text": "Let me get a human specialist for you" }
  ]
}`

export const EXAMPLE_RESPONSE = `{
  "status": "STORED",
  "handoff_id": "7f92a136cc204830a0459dc12c4a519b",
  "transfer_to": "+14155550142",
  "expires_in": 120
}`

/** Plain-text setup steps for the developer, in the same words as the dialog. */
export const integrationInstructions = (url: string): string =>
  [
    'Connect your AI agent: Plivo CX transfer context',
    '',
    `Endpoint: POST ${url}`,
    'Every request: Authorization: Bearer <API key>',
    '',
    'When the customer calls your Plivo CX number',
    `1. A Call Forward step dials your AI agent's SIP address. The SIP header ${SIP_HEADER} carries the handoff_id.`,
    '2. POST the transfer context with that handoff_id and original_caller (the number the call came from), then hang up. The call continues to Assign to human.',
    '',
    "When the customer calls your AI agent's number",
    '1. POST the transfer context with transfer_to: your Plivo CX number you will transfer the call to, and transfer_from: the caller ID your transfer call will show. Pick your own handoff_id (a UUID v4, 32 lowercase hex characters) so you can safely resend the request.',
    ...EXAMPLE_REQUEST.split('\n').map((line) => `   ${line}`),
    '   transfer_to is held for this call for 2 minutes:',
    ...EXAMPLE_RESPONSE.split('\n').map((line) => `   ${line}`),
    '2. After a 202 or 200, transfer the call to transfer_to from transfer_from, by phone or SIP.',
    '   503 or no response: resend the same request after 2 seconds.',
    "   Without a 202 or 200, do not transfer to transfer_to: the call could pick up another transfer's context.",
    '',
    'Updates: send every field again with the same handoff_id and revision + 1 (an older revision gets 409).',
    "original_caller is the customer's number in E.164 format; leave it out for withheld callers.",
    '',
    'Optional, only when you transfer the call in and your platform stays on it: wait for a human specialist',
    `   GET ${url}/<handoff ID>/status until it returns "accepted", then leave the call.`,
    '   When the customer calls your Plivo CX number, the specialist rings only after you hang up, so do not wait.',
  ].join('\n')

/** Poll only while a fresh pending policy could still receive context; afterwards rely on events. */
export const pollWhilePending = (
  data: HandoffContext | undefined,
  now: number = Date.now(),
): boolean =>
  !!data &&
  data.status === 'pending' &&
  !!data.pending_since &&
  now - Date.parse(data.pending_since) <= NOT_RECEIVED_GRACE_MS

export type PanelState =
  | 'hidden'
  | 'pending'
  | 'not_received'
  | 'available'
  | 'unavailable'
  | 'error'

/** What the agent panel shows for a read result; the grace timer is a UI default, never a call wait. */
export const panelState = (
  data: HandoffContext | undefined,
  isError: boolean,
  now: number = Date.now(),
  httpStatus?: number,
): PanelState => {
  if (isError) {
    // Authorization/deletion wins over cached data. Only transient failures retain a prior view.
    if ([403, 404].includes(httpStatus ?? 0)) return 'hidden'
    if (!data || (httpStatus !== undefined && httpStatus < 500)) {
      return httpStatus === 503 ? 'hidden' : 'error'
    }
  }
  if (!data) return 'hidden'
  const map: Record<HandoffContextStatus, PanelState> = {
    not_configured: 'hidden',
    available: 'available',
    unavailable: 'unavailable',
    pending: pollWhilePending(data, now) ? 'pending' : 'not_received',
  }
  return map[data.status]
}

/** The wire has no turn IDs. Content + duplicate occurrence stays stable when other turns move. */
export const transcriptRows = (turns: NonNullable<HandoffContextBlock['transcript']>) => {
  const occurrences = new Map<string, number>()
  return turns.map((turn) => {
    const identity = JSON.stringify([turn.speaker, turn.text, turn.offset_ms ?? null])
    const occurrence = occurrences.get(identity) || 0
    occurrences.set(identity, occurrence + 1)
    return { turn, key: `${identity}:${occurrence}` }
  })
}
